import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Check, Plus } from 'lucide-react'
import clsx from 'clsx'

export function ComposerAccountToggle({
  name,
  icon,
  selected,
  onClick,
}: {
  name: string
  icon: ReactNode
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={clsx(
        'group inline-flex h-9 min-w-0 max-w-[220px] items-center gap-2 rounded-full px-2 text-left transition-all duration-200 active:scale-[0.96]',
        selected
          ? 'bg-accent-tint text-accent-ink shadow-hairline ring-1 ring-accent/40'
          : 'bg-surface text-ink-600 shadow-hairline hover:bg-hover hover:text-ink-900',
      )}
    >
      <span
        className={clsx(
          'grid size-6 shrink-0 place-items-center rounded-full',
          selected ? 'bg-surface text-accent-ink shadow-hairline' : 'bg-inset text-ink-700 shadow-hairline',
        )}
      >
        <span className="inline-flex size-3.5 items-center justify-center">{icon}</span>
      </span>
      <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium">{name}</span>
      <span
        className={clsx(
          'grid size-[18px] shrink-0 place-items-center rounded-full transition-all',
          selected
            ? 'bg-accent text-white'
            : 'bg-inset text-transparent shadow-hairline group-hover:text-ink-300',
        )}
      >
        <Check size={11} strokeWidth={2.8} />
      </span>
    </button>
  )
}

export function ComposerAddAccount({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-9 items-center gap-1.5 rounded-full bg-surface px-3 text-[12px] font-medium text-ink-500 shadow-hairline transition-all hover:bg-hover hover:text-ink-900 active:scale-[0.96]"
    >
      <Plus size={14} strokeWidth={2.2} />
      <span>Add channel</span>
    </button>
  )
}

export function ComposerActionRail({ children }: { children: ReactNode }) {
  return (
    <div className="inline-flex min-h-9 max-w-full flex-wrap items-center gap-0.5 rounded-full bg-surface p-1 shadow-card">
      {children}
    </div>
  )
}

export function ComposerAction({
  icon,
  children,
  active = false,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
    icon?: ReactNode
    active?: boolean
  }) {
  return (
    <button
      type="button"
      className={clsx(
        'inline-flex h-7 items-center gap-1.5 rounded-full px-2.5 text-[11.5px] font-normal transition-[background-color,color,transform] duration-150 active:scale-[0.96] disabled:pointer-events-none disabled:opacity-40',
        active
          ? 'bg-surface text-ink-900 shadow-card'
          : 'text-ink-500 hover:bg-surface hover:text-ink-900',
        className,
      )}
      {...props}
    >
      {icon && <span className="inline-flex size-3.5 items-center justify-center">{icon}</span>}
      <span>{children}</span>
    </button>
  )
}

export function ComposerFooterButton({
  variant = 'secondary',
  icon,
  children,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'quiet'
  icon?: ReactNode
}) {
  return (
    <button
      type="button"
      className={clsx(
        'inline-flex h-9 items-center justify-center gap-1.5 rounded-full px-3.5 text-[12px] font-medium transition-all active:scale-[0.96] disabled:pointer-events-none disabled:opacity-40',
        variant === 'primary' && 'bg-accent text-white shadow-btn hover:bg-accent-ink',
        variant === 'secondary' && 'bg-surface text-ink-800 shadow-btn hover:bg-hover',
        variant === 'quiet' && 'text-ink-500 hover:bg-hover hover:text-ink-900',
        className,
      )}
      {...props}
    >
      {icon && <span className="inline-flex size-3.5 items-center justify-center">{icon}</span>}
      <span>{children}</span>
    </button>
  )
}
